/**
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @description Tax groups service for API operations
 */

import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import type {
  TaxGroup,
  TaxGroupCreateUpdateResponse,
  TaxGroupDeleteResponse,
  TaxGroupDetailResponse,
  TaxGroupForm,
  TaxGroupListResponse,
  TaxGroupNameValidationResponse,
  TaxGroupResponse,
} from '../types/taxGroup.interface';

/**
 * Get all tax groups for the authenticated user
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup[]
 */
export const getTaxGroupsService = async (): Promise<TaxGroup[]> => {
  const response = await apiMunzen.get<TaxGroupListResponse>('/rubros-impuestos');
  return response.data.data;
};

/**
 * Get a specific tax group with all available taxes and selected status
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroupResponse
 */
export const getTaxGroupService = async (id: number): Promise<TaxGroupResponse> => {
  const response = await apiMunzen.get<TaxGroupDetailResponse>(`/rubros-impuestos/${id}`);
  return response.data.data;
};

/**
 * Create a new tax group
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup
 */
export const createTaxGroupService = async (data: TaxGroupForm): Promise<TaxGroup> => {
  const response = await apiMunzen.post<TaxGroupCreateUpdateResponse>('/rubros-impuestos', data);
  return response.data.data;
};

/**
 * Update an existing tax group
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup
 */
export const updateTaxGroupService = async (id: number, data: TaxGroupForm): Promise<TaxGroup> => {
  const response = await apiMunzen.put<TaxGroupCreateUpdateResponse>(
    `/rubros-impuestos/${id}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete a tax group (only if not assigned to any products/services)
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroupDeleteResponse
 */
export const deleteTaxGroupService = async (id: number): Promise<TaxGroupDeleteResponse> => {
  const response = await apiMunzen.delete<TaxGroupDeleteResponse>(`/rubros-impuestos/${id}`);
  return response.data;
};

/**
 * Get tax groups that only have transferred taxes (no retained or local taxes)
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup[]
 */
export const getTransferredTaxesGroupsService = async (): Promise<TaxGroup[]> => {
  const response = await apiMunzen.get<TaxGroupListResponse>(
    '/rubros-impuestos/get-impuestos-trasladados',
  );
  return response.data.data;
};

/**
 * Validate if a tax group name is unique within an enterprise
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns { is_unique: boolean }
 */
export const validateTaxGroupNameService = async (
  name: string,
  enterpriseId: number,
  taxGroupId?: number,
): Promise<{ is_unique: boolean }> => {
  const data = {
    name,
    enterprise_id: enterpriseId,
    ...(taxGroupId && { rubro_impuesto_id: taxGroupId }),
  };

  const response = await apiMunzen.post<TaxGroupNameValidationResponse>(
    '/rubros-impuestos/validate-unique-name',
    data,
  );
  return response.data.data;
};

/**
 * Initial configuration endpoints for new clients
 */

/**
 * Get all tax groups for new client initial configuration
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup[]
 */
export const getNewClientTaxGroupsService = async (newClientToken: string): Promise<TaxGroup[]> => {
  const response = await apiMunzen.get<TaxGroupListResponse>(
    `/new-client/rubros-impuesto/${newClientToken}`,
  );
  return response.data.data;
};

/**
 * Create tax group for new client initial configuration
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup
 */
export const createNewClientTaxGroupService = async (
  newClientToken: string,
  data: TaxGroupForm,
): Promise<TaxGroup> => {
  const response = await apiMunzen.post<TaxGroupCreateUpdateResponse>(
    `/new-client/rubros-impuesto/${newClientToken}`,
    data,
  );
  return response.data.data;
};

/**
 * Update tax group for new client initial configuration
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroup
 */
export const updateNewClientTaxGroupService = async (
  taxGroupId: number,
  newClientToken: string,
  data: TaxGroupForm,
): Promise<TaxGroup> => {
  const response = await apiMunzen.put<TaxGroupCreateUpdateResponse>(
    `/new-client/rubros-impuesto/${taxGroupId}/${newClientToken}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete tax group for new client initial configuration
 *
 * @author Omar Sánchez Martínez
 * @since 26/07/2025
 * @returns TaxGroupDeleteResponse
 */
export const deleteNewClientTaxGroupService = async (
  taxGroupId: number,
  newClientToken: string,
): Promise<TaxGroupDeleteResponse> => {
  const response = await apiMunzen.delete<TaxGroupDeleteResponse>(
    `/new-client/rubros-impuesto/${taxGroupId}/${newClientToken}`,
  );
  return response.data;
};
